// Per-member mounted rigs: a rider astride a bespoke mount, one group per squad member.
// Fast cavalry and "rider" squads render through here instead of the shared humanoid rig
// (plan §29.4 — fully procedural, no asset files). The renderer animates the named parts:
//   legFL / legFR / legBL / legBR — gallopers (rotate.x by gait phase)
//   wingL / wingR                 — flyers (flap rotate.z)
//   tail                          — optional sway
//   rider                         — seated warrior (bob), with child `riderArm` (lance thrust)

import * as THREE from 'three';
import { makeBowGeo, makeCrossbowGeo, OWNER_COLORS } from './unitStyles';

const SKIN = 0xc99b72;

function mix(a: number, b: number, t: number): THREE.Color {
  return new THREE.Color(a).lerp(new THREE.Color(b), t);
}
function mat(color: THREE.Color | number, rough = 0.9, metal = 0.05): THREE.MeshStandardMaterial {
  return new THREE.MeshStandardMaterial({
    color: color instanceof THREE.Color ? color : new THREE.Color(color),
    roughness: rough,
    metalness: metal,
    flatShading: true,
  });
}
function glow(color: number, i = 1.1): THREE.MeshStandardMaterial {
  return new THREE.MeshStandardMaterial({ color, emissive: color, emissiveIntensity: i, roughness: 0.3 });
}

export type MountKind =
  | 'horse'
  | 'armored-horse'
  | 'skeletal-horse'
  | 'gryphon'
  | 'bone-serpent'
  | 'astral-steed'
  | 'comet-disc'
  | 'crystal-steed'
  | 'great-stag';

const KIND: Record<string, MountKind> = {
  'u-ic-royal-scouts': 'horse',
  'u-ic-iron-cavalry': 'armored-horse',
  'u-ic-gryphon-lancers': 'gryphon',
  'u-ah-dread-cavalry': 'skeletal-horse',
  'u-ah-bonewyrm-riders': 'bone-serpent',
  'u-ad-star-lancer': 'astral-steed',
  'u-ad-comet-riders': 'comet-disc',
  'u-ad-chrono-lancers': 'crystal-steed',
  'u-vw-canopy-riders': 'great-stag',
};

export const MOUNTED_DEFS = new Set(Object.keys(KIND));
const FLYERS = new Set(['u-ic-gryphon-lancers', 'u-ah-bonewyrm-riders', 'u-ad-comet-riders']);

export function isFlyingMount(defId: string): boolean {
  return FLYERS.has(defId);
}

// ---------------------------------------------------------------- shared pieces

/** A two-segment leg as a named hip group; rotate the group about x for the gait. */
function legGroup(m: THREE.Material, name: string, x: number, z: number, len = 0.55): THREE.Group {
  const hip = new THREE.Group();
  hip.name = name;
  hip.position.set(x, 0.9, z);
  const upper = new THREE.Mesh(new THREE.CylinderGeometry(0.1, 0.08, len, 6), m);
  upper.position.y = -len / 2;
  hip.add(upper);
  const lower = new THREE.Mesh(new THREE.CylinderGeometry(0.07, 0.05, len, 6), m);
  lower.position.set(0, -len - 0.02, 0.04);
  hip.add(lower);
  const hoof = new THREE.Mesh(new THREE.BoxGeometry(0.14, 0.12, 0.18), m);
  hoof.position.set(0, -len * 2 + 0.02, 0.06);
  hip.add(hoof);
  return hip;
}

/** Seated warrior with a forward lance; `riderArm` is the animatable lance arm. */
function addRider(
  parent: THREE.Object3D,
  owner: 0 | 1,
  saddleY: number,
  opts: {
    body: number;
    helm: 'dome' | 'spike' | 'horn' | 'hood' | 'circlet' | null;
    lanceColor: number;
    tint?: number;
    cape?: boolean;
    weapon?: 'lance' | 'bow' | 'crossbow';
  },
): void {
  const rider = new THREE.Group();
  rider.name = 'rider';
  const bodyC = mix(opts.body, OWNER_COLORS[owner], opts.tint ?? 0.22);
  const bmat = mat(bodyC, 0.85);
  const limb = mat(bodyC.clone().multiplyScalar(0.6), 0.85);

  const torso = new THREE.Mesh(new THREE.CylinderGeometry(0.17, 0.22, 0.62, 8), bmat);
  torso.position.y = 0.44;
  torso.rotation.x = 0.14;
  rider.add(torso);
  const head = new THREE.Mesh(new THREE.SphereGeometry(0.16, 10, 8), mat(SKIN, 0.8));
  head.position.set(0, 0.88, 0.05);
  rider.add(head);
  switch (opts.helm) {
    case 'dome': {
      const h = new THREE.Mesh(new THREE.SphereGeometry(0.17, 8, 6, 0, Math.PI * 2, 0, Math.PI / 2), bmat);
      h.position.set(0, 0.9, 0.05);
      rider.add(h);
      break;
    }
    case 'spike': {
      const h = new THREE.Mesh(new THREE.ConeGeometry(0.17, 0.36, 6), bmat);
      h.position.set(0, 1.0, 0.05);
      rider.add(h);
      break;
    }
    case 'horn': {
      const cap = new THREE.Mesh(new THREE.SphereGeometry(0.17, 8, 6, 0, Math.PI * 2, 0, Math.PI / 2), bmat);
      cap.position.set(0, 0.9, 0.05);
      rider.add(cap);
      for (const sx of [-1, 1]) {
        const c = new THREE.Mesh(new THREE.ConeGeometry(0.05, 0.26, 5), mat(0xe0d8c0, 0.6));
        c.position.set(sx * 0.15, 0.98, 0.03);
        c.rotation.z = sx * 0.8;
        rider.add(c);
      }
      break;
    }
    case 'hood': {
      const h = new THREE.Mesh(new THREE.ConeGeometry(0.2, 0.44, 7), bmat);
      h.position.set(0, 0.94, -0.02);
      rider.add(h);
      break;
    }
    case 'circlet': {
      const ring = new THREE.Mesh(new THREE.TorusGeometry(0.16, 0.03, 6, 12), glow(opts.lanceColor, 0.9));
      ring.rotation.x = Math.PI / 2;
      ring.position.set(0, 0.98, 0.05);
      rider.add(ring);
      break;
    }
    default:
      break;
  }
  if (opts.cape) {
    const cape = new THREE.Mesh(new THREE.PlaneGeometry(0.44, 0.62), new THREE.MeshStandardMaterial({ color: OWNER_COLORS[owner], side: THREE.DoubleSide, roughness: 0.9 }));
    cape.position.set(0, 0.42, -0.2);
    cape.rotation.x = 0.25;
    rider.add(cape);
  }
  // legs astride the barrel
  for (const sx of [-1, 1]) {
    const thigh = new THREE.Mesh(new THREE.CylinderGeometry(0.09, 0.07, 0.52, 6), limb);
    thigh.position.set(sx * 0.2, 0.18, 0.04);
    thigh.rotation.z = sx * 0.55;
    thigh.rotation.x = 0.5;
    rider.add(thigh);
  }
  // left arm holds the reins; right arm is the lance arm
  const reins = new THREE.Mesh(new THREE.CylinderGeometry(0.06, 0.05, 0.5, 6), limb);
  reins.position.set(-0.2, 0.52, 0.16);
  reins.rotation.x = 1.0;
  rider.add(reins);
  const arm = new THREE.Group();
  arm.name = 'riderArm';
  arm.position.set(0.2, 0.58, 0.06);
  const upper = new THREE.Mesh(new THREE.CylinderGeometry(0.06, 0.05, 0.5, 6), limb);
  upper.position.y = -0.08;
  upper.rotation.x = 1.35;
  arm.add(upper);
  if (opts.weapon === 'bow') {
    const bow = new THREE.Mesh(makeBowGeo(), mat(opts.lanceColor, 0.8));
    bow.scale.setScalar(0.72);
    bow.position.set(0, -0.12, 0.2);
    bow.rotation.z = -0.12;
    arm.add(bow);
  } else if (opts.weapon === 'crossbow') {
    const crossbow = new THREE.Mesh(makeCrossbowGeo(), mat(opts.lanceColor, 0.72, 0.18));
    crossbow.scale.setScalar(0.58);
    crossbow.position.set(0, -0.12, 0.28);
    arm.add(crossbow);
  } else {
    const lance = new THREE.Mesh(new THREE.CylinderGeometry(0.035, 0.05, 1.9, 6), mat(opts.lanceColor, 0.5, 0.4));
    lance.rotation.x = Math.PI / 2;
    lance.position.set(0, -0.12, 0.75);
    arm.add(lance);
    const tip = new THREE.Mesh(new THREE.ConeGeometry(0.08, 0.3, 6), mat(0xeef0f4, 0.4, 0.5));
    tip.rotation.x = Math.PI / 2;
    tip.position.set(0, -0.12, 1.75);
    arm.add(tip);
  }
  rider.add(arm);
  rider.position.y = saddleY;
  parent.add(rider);
}

// ---------------------------------------------------------------- mounts

/** Four-legged barrel body with named legs; returns saddle height for the rider. */
function quadBody(g: THREE.Group, body: THREE.Material, opts: { len: number; girth: number; legLen?: number }): number {
  const legLen = opts.legLen ?? 0.55;
  const barrel = new THREE.Mesh(new THREE.CapsuleGeometry(opts.girth, opts.len, 5, 10), body);
  barrel.name = 'mountBody';
  barrel.rotation.x = Math.PI / 2; // lie the barrel along +z (forward), not side-to-side
  barrel.position.y = 0.98;
  g.add(barrel);
  const hz = opts.len / 2 - 0.02;
  const hx = opts.girth * 0.7;
  g.add(legGroup(body, 'legFL', -hx, hz, legLen));
  g.add(legGroup(body, 'legFR', hx, hz, legLen));
  g.add(legGroup(body, 'legBL', -hx, -hz, legLen));
  g.add(legGroup(body, 'legBR', hx, -hz, legLen));
  return 0.98 + opts.girth * 0.55;
}

function addHorseHeadNeck(g: THREE.Group, body: THREE.Material, len: number, maneColor: number): void {
  const neck = new THREE.Mesh(new THREE.CylinderGeometry(0.14, 0.2, 0.7, 7), body);
  neck.position.set(0, 1.35, len / 2 + 0.05);
  neck.rotation.x = -0.7;
  g.add(neck);
  const head = new THREE.Mesh(new THREE.BoxGeometry(0.22, 0.24, 0.5), body);
  head.name = 'mountHead';
  head.position.set(0, 1.62, len / 2 + 0.36);
  head.rotation.x = 0.35;
  g.add(head);
  for (const sx of [-1, 1]) {
    const ear = new THREE.Mesh(new THREE.ConeGeometry(0.05, 0.16, 5), body);
    ear.position.set(sx * 0.08, 1.78, len / 2 + 0.2);
    g.add(ear);
  }
  const mane = new THREE.Mesh(new THREE.BoxGeometry(0.06, 0.5, 0.55), mat(maneColor, 0.95));
  mane.position.set(0, 1.42, len / 2 - 0.02);
  mane.rotation.x = -0.7;
  g.add(mane);
}

function tailBox(color: number, x: number, y: number, z: number): THREE.Group {
  const tail = new THREE.Group();
  tail.name = 'tail';
  tail.position.set(x, y, z);
  const t = new THREE.Mesh(new THREE.CylinderGeometry(0.08, 0.02, 0.6, 6), mat(color, 0.95));
  t.position.set(0, -0.2, -0.15);
  t.rotation.x = 0.7;
  tail.add(t);
  return tail;
}

function buildMount(kind: MountKind, owner: 0 | 1): { group: THREE.Group; saddleY: number } {
  const g = new THREE.Group();
  const team = OWNER_COLORS[owner];
  switch (kind) {
    case 'horse': {
      const hide = mat(mix(0x5a4632, team, 0.12), 0.85);
      const saddleY = quadBody(g, hide, { len: 1.2, girth: 0.42 });
      addHorseHeadNeck(g, hide, 1.2, 0x2a2018);
      g.add(tailBox(0x2a2018, 0, 1.1, -0.72));
      // barding skirt in team color hugging each flank (thin in x, long along the body)
      for (const sx of [-1, 1]) {
        const bard = new THREE.Mesh(new THREE.BoxGeometry(0.05, 0.5, 0.9), mat(team, 0.7));
        bard.position.set(sx * 0.46, 0.82, 0);
        g.add(bard);
      }
      return { group: g, saddleY };
    }
    case 'armored-horse': {
      const hide = mat(mix(0x4c4034, team, 0.1), 0.86);
      const saddleY = quadBody(g, hide, { len: 1.36, girth: 0.5, legLen: 0.58 });
      addHorseHeadNeck(g, hide, 1.36, 0x211c18);
      g.add(tailBox(0x211c18, 0, 1.12, -0.82));
      const steel = mat(mix(0x69737e, team, 0.18), 0.48, 0.58);
      const darkSteel = mat(0x30363d, 0.55, 0.58);
      // Overlapping flank plates and a closed chamfron give the cavalry a much broader read.
      for (const sx of [-1, 1]) {
        for (let i = 0; i < 4; i++) {
          const plate = new THREE.Mesh(new THREE.BoxGeometry(0.08, 0.52, 0.48), i % 2 ? darkSteel : steel);
          plate.position.set(sx * 0.54, 0.9, -0.55 + i * 0.37);
          plate.rotation.x = (i - 1.5) * 0.05;
          g.add(plate);
        }
      }
      const faceplate = new THREE.Mesh(new THREE.BoxGeometry(0.3, 0.34, 0.58), steel);
      faceplate.position.set(0, 1.64, 1.08);
      faceplate.rotation.x = 0.34;
      g.add(faceplate);
      const brow = new THREE.Mesh(new THREE.BoxGeometry(0.34, 0.08, 0.3), darkSteel);
      brow.position.set(0, 1.72, 1.23);
      brow.rotation.x = 0.34;
      g.add(brow);
      const blanket = new THREE.Mesh(new THREE.BoxGeometry(0.92, 0.09, 0.92), mat(team, 0.82));
      blanket.position.set(0, saddleY - 0.08, -0.05);
      g.add(blanket);
      return { group: g, saddleY };
    }
    case 'skeletal-horse': {
      const bone = mat(0xd8d0bc, 0.9);
      const saddleY = quadBody(g, bone, { len: 1.15, girth: 0.34 });
      addHorseHeadNeck(g, bone, 1.15, 0x6a2030);
      // exposed ribs
      for (let i = 0; i < 4; i++) {
        const rib = new THREE.Mesh(new THREE.TorusGeometry(0.32 - i * 0.02, 0.03, 5, 10, Math.PI), bone);
        rib.rotation.y = Math.PI / 2;
        rib.position.set(0, 0.98, 0.3 - i * 0.2);
        g.add(rib);
      }
      // ghostfire eyes
      for (const sx of [-1, 1]) {
        const eye = new THREE.Mesh(new THREE.SphereGeometry(0.05, 6, 5), glow(0x8affc0, 1.4));
        eye.position.set(sx * 0.07, 1.66, 0.95);
        g.add(eye);
      }
      return { group: g, saddleY };
    }
    case 'great-stag': {
      const hide = mat(mix(0x6b7a3c, team, 0.12), 0.9);
      const saddleY = quadBody(g, hide, { len: 1.15, girth: 0.38, legLen: 0.6 });
      addHorseHeadNeck(g, hide, 1.15, 0x3a4a24);
      // antlers
      for (const sx of [-1, 1]) {
        const beam = new THREE.Mesh(new THREE.CylinderGeometry(0.04, 0.05, 0.5, 5), mat(0x8a7a50, 0.9));
        beam.position.set(sx * 0.1, 1.9, 1.2 * 0.5 + 0.1);
        beam.rotation.z = sx * 0.5;
        g.add(beam);
        for (let k = 0; k < 2; k++) {
          const tine = new THREE.Mesh(new THREE.ConeGeometry(0.03, 0.22, 5), mat(0x9a8a5c, 0.9));
          tine.position.set(sx * (0.22 + k * 0.08), 2.0 + k * 0.12, 0.7);
          tine.rotation.z = sx * 1.0;
          g.add(tine);
        }
      }
      // leaf saddle blanket
      const blanket = new THREE.Mesh(new THREE.BoxGeometry(0.8, 0.08, 0.7), mat(team, 0.8));
      blanket.position.set(0, saddleY - 0.08, 0);
      g.add(blanket);
      return { group: g, saddleY };
    }
    case 'crystal-steed': {
      const shell = mat(mix(0x6ec8c0, team, 0.2), 0.35, 0.3);
      const saddleY = quadBody(g, shell, { len: 1.1, girth: 0.36 });
      addHorseHeadNeck(g, shell, 1.1, 0x9ff0e6);
      // faceted crystal crest along the spine
      for (let i = 0; i < 3; i++) {
        const shard = new THREE.Mesh(new THREE.OctahedronGeometry(0.16 - i * 0.02, 0), glow(0x9ff0e6, 0.7));
        shard.position.set(0, 1.42, 0.25 - i * 0.28);
        g.add(shard);
      }
      return { group: g, saddleY };
    }
    case 'astral-steed': {
      const shell = mat(mix(0x9aa8e0, team, 0.2), 0.4, 0.35);
      const saddleY = quadBody(g, shell, { len: 1.2, girth: 0.4 });
      addHorseHeadNeck(g, shell, 1.2, 0xcfe0ff);
      // starfire mane + trailing motes
      const glowMane = new THREE.Mesh(new THREE.BoxGeometry(0.05, 0.55, 0.6), glow(0xbcd0ff, 0.8));
      glowMane.position.set(0, 1.45, 0.55);
      glowMane.rotation.x = -0.7;
      g.add(glowMane);
      g.add(tailBox(0xbcd0ff, 0, 1.1, -0.74));
      return { group: g, saddleY };
    }
    case 'gryphon': {
      const feather = mat(mix(0xe8dcb0, team, 0.14), 0.85);
      const saddleY = quadBody(g, feather, { len: 1.0, girth: 0.38, legLen: 0.4 });
      // eagle head
      const head = new THREE.Mesh(new THREE.SphereGeometry(0.22, 9, 7), feather);
      head.position.set(0, 1.5, 0.62);
      g.add(head);
      const beak = new THREE.Mesh(new THREE.ConeGeometry(0.1, 0.28, 6), mat(0xe0b040, 0.5));
      beak.rotation.x = Math.PI / 2;
      beak.position.set(0, 1.46, 0.86);
      g.add(beak);
      // wings (animatable)
      for (const [sx, name] of [[-1, 'wingL'], [1, 'wingR']] as const) {
        const wing = new THREE.Group();
        wing.name = name;
        wing.position.set((sx as number) * 0.32, 1.15, 0.05);
        const pane = new THREE.Mesh(new THREE.BoxGeometry(1.1, 0.08, 0.7), feather);
        pane.position.set((sx as number) * 0.55, 0, 0);
        wing.add(pane);
        g.add(wing);
      }
      g.add(tailBox(0xc8b070, 0, 1.05, -0.6));
      return { group: g, saddleY };
    }
    case 'bone-serpent': {
      const bone = mat(0xcfc6ae, 0.9);
      // segmented serpentine body arcing forward, no legs
      const segs = 6;
      for (let i = 0; i < segs; i++) {
        const r = 0.34 - i * 0.03;
        const seg = new THREE.Mesh(new THREE.SphereGeometry(r, 9, 7), bone);
        const z = 0.7 - i * 0.34;
        seg.position.set(0, 1.0 + Math.sin(i * 0.6) * 0.12, z);
        g.add(seg);
      }
      // skull head
      const skull = new THREE.Mesh(new THREE.BoxGeometry(0.34, 0.3, 0.44), bone);
      skull.position.set(0, 1.12, 0.95);
      g.add(skull);
      const jaw = new THREE.Mesh(new THREE.BoxGeometry(0.3, 0.12, 0.36), bone);
      jaw.position.set(0, 0.98, 1.02);
      g.add(jaw);
      for (const sx of [-1, 1]) {
        const eye = new THREE.Mesh(new THREE.SphereGeometry(0.05, 6, 5), glow(0x8affc0, 1.4));
        eye.position.set(sx * 0.1, 1.18, 1.12);
        g.add(eye);
      }
      // ribbed spine ridge (team-tinted)
      for (let i = 0; i < 4; i++) {
        const fin = new THREE.Mesh(new THREE.ConeGeometry(0.06, 0.3, 5), mat(mix(0x7a6a5a, team, 0.4), 0.85));
        fin.position.set(0, 1.35, 0.4 - i * 0.3);
        g.add(fin);
      }
      g.add(tailBox(0xcfc6ae, 0, 1.0, -1.28));
      return { group: g, saddleY: 1.34 };
    }
    case 'comet-disc': {
      // hovering meteor-shard board, no legs
      const disc = new THREE.Mesh(new THREE.CylinderGeometry(0.62, 0.5, 0.22, 8), mat(mix(0x6a5238, team, 0.18), 0.8));
      disc.position.y = 0.55;
      g.add(disc);
      const rim = new THREE.Mesh(new THREE.TorusGeometry(0.6, 0.06, 6, 16), glow(0xffb060, 0.8));
      rim.rotation.x = Math.PI / 2;
      rim.position.y = 0.55;
      rim.name = 'tail'; // reuse tail slot for a slow spin
      g.add(rim);
      // trailing comet flame
      const flame = new THREE.Mesh(new THREE.ConeGeometry(0.24, 0.9, 7), glow(0xff8a3f, 0.9));
      flame.rotation.x = -Math.PI / 2;
      flame.position.set(0, 0.55, -0.7);
      g.add(flame);
      return { group: g, saddleY: 0.72 };
    }
    default:
      return { group: g, saddleY: 1.1 };
  }
}

// ---------------------------------------------------------------- factory

export function makeMountRig(defId: string, owner: 0 | 1, uiScale = 1): THREE.Group {
  const kind = KIND[defId] ?? 'horse';
  const { group, saddleY } = buildMount(kind, owner);

  // rider flavor per faction / unit
  const riderOpts: Parameters<typeof addRider>[3] =
    defId === 'u-ic-royal-scouts'
      ? { body: 0x7890a8, helm: 'dome', lanceColor: 0x7a5e3a, tint: 0.18, weapon: 'crossbow' }
      : defId === 'u-ic-iron-cavalry'
      ? { body: 0x7a92a8, helm: 'spike', lanceColor: 0xb0b8c4, cape: true }
      : defId === 'u-ic-gryphon-lancers'
        ? { body: 0xe8dcb0, helm: 'dome', lanceColor: 0xe8dcb0, cape: true }
        : defId === 'u-ah-dread-cavalry'
          ? { body: 0x704a5a, helm: 'horn', lanceColor: 0x9a6a7a, tint: 0.18 }
          : defId === 'u-ah-bonewyrm-riders'
            ? { body: 0xb0a890, helm: 'hood', lanceColor: 0x8affc0, tint: 0.16 }
            : defId === 'u-ad-star-lancer'
              ? { body: 0xa8c8f0, helm: 'circlet', lanceColor: 0x9ff0ff, cape: true }
              : defId === 'u-ad-comet-riders'
                ? { body: 0xf0cf8a, helm: 'circlet', lanceColor: 0xffb060 }
                : defId === 'u-ad-chrono-lancers'
                  ? { body: 0x74d8c0, helm: 'circlet', lanceColor: 0x9ff0e6 }
                  : { body: 0x8ab86a, helm: 'hood', lanceColor: 0xbfe08a, tint: 0.18, weapon: 'bow' }; // canopy riders
  addRider(group, owner, saddleY, riderOpts);

  if (defId === 'u-ic-royal-scouts') {
    // Map roll, signal horn, and compact saddlebags distinguish reconnaissance gear.
    for (const sx of [-1, 1]) {
      const bag = new THREE.Mesh(new THREE.BoxGeometry(0.28, 0.3, 0.34), mat(0x5b4630, 0.92));
      bag.position.set(sx * 0.43, saddleY - 0.24, -0.38);
      group.add(bag);
    }
    const map = new THREE.Mesh(new THREE.CylinderGeometry(0.055, 0.055, 0.5, 7), mat(0xe5d2a0, 0.9));
    map.rotation.z = Math.PI / 2;
    map.position.set(-0.34, saddleY - 0.02, -0.18);
    group.add(map);
    const horn = new THREE.Mesh(new THREE.ConeGeometry(0.09, 0.46, 8, 1, true), mat(0xc89b4b, 0.5, 0.45));
    horn.rotation.x = Math.PI / 2;
    horn.position.set(0.34, saddleY, -0.22);
    group.add(horn);
  }

  // small team standard on a short pole behind the saddle so allegiance always reads
  const pole = new THREE.Mesh(new THREE.CylinderGeometry(0.02, 0.02, 0.7, 5), mat(0x3a2f26, 0.9));
  pole.position.set(-0.12, saddleY + 0.35, -0.24);
  group.add(pole);
  const flag = new THREE.Mesh(
    new THREE.PlaneGeometry(0.3, 0.2),
    new THREE.MeshBasicMaterial({ color: OWNER_COLORS[owner], side: THREE.DoubleSide }),
  );
  flag.position.set(0.03, saddleY + 0.6, -0.24);
  group.add(flag);

  group.scale.setScalar(uiScale);
  group.traverse((o) => {
    if (o instanceof THREE.Mesh && o.material instanceof THREE.MeshStandardMaterial) {
      o.castShadow = true;
      o.receiveShadow = true;
    }
  });
  return group;
}
